import { Cancel, Close, DoneAll, Refresh } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, CircularProgress, FormControl, IconButton, Modal, Paper, SxProps, Typography } from "@mui/material"
import { Fragment, ReactNode, useEffect, useRef, useState } from "react"
import { exactDigitCountValidationFn, greaterThanEqualToValueValidationFn, greaterThanValueValidationFn, parseFalsyFields } from '../utils/ValidationUtils';
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { HubBikeModel, HubModel, OrderModel, OrderUploadLabels, RideStatus } from "../utils/Models";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import { useSelector } from "react-redux";
import { getHubSelection } from "../../features/hubSelection";
import { toFixed2, toIstDateString } from "../utils/CommonUtils";

// Interfaces to store state and constants
interface LocalState {
  loading?: boolean;
  error?: string;
  bikeData?: HubBikeModel[],
  rcOptions?: BikeRcData[];
  smsState?: 'PROGRESS' | 'DONE';
}
interface OldBikeRcData {
  odStart: number, odEnd?: number, rcNumber: string
}
interface BikeRcData {
  odStart: number, rcNumber: string, lastServiced?: number, used?: boolean, conflictingOrder?: number
}
interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  otp?: string;
  selectedBikeId?: number;
  oldRcData: OldBikeRcData[];
  useCustomAmount?: boolean;
  extraAmount: number;
  oldBikeRemainingDurationAmount: number;
  newBikeRemainingDurationAmount: number;
  changeTime: number;
  uploads: { [k in OrderUploadLabels]?: string };
  rcData: BikeRcData[];
  errors: { otp?: string };
}

// Start booking component
export function EditBooking(props: {
  order: OrderModel,
  children: ReactNode,
  style: any,
  hub: HubModel
  backFn: () => void
}) {

  // Refs and consts
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const bikeId = (props.order.bikes?.[0]?.id || '').toString();
  const bikeName = `${(props.order.bikes?.[0]?.brand || '')} ${(props.order.bikes?.[0]?.name || '')}`;
  const bikeCount = props.order?.bikes?.[0]?.order_bikes?.quantity || 0;
  const existingRcData = props.order.bikeDetails;
  const accessoryData = props.order.accessories?.map(a => ({ id: a.id, quantity: a.order_accessories?.quantity })).filter(a => a.quantity) || [];

  // Global state
  const selectedHub = useSelector(getHubSelection);

  // Local state
  const [localState, setLocalState] = useState<LocalState>({});
  const [formState, setFormState] = useState<FormState>({
    errors: {},
    selectedBikeId: undefined,
    // selectedBikeId: bikeId ? Number(bikeId) : undefined,
    rcData: Array(bikeCount).fill(0).map(a => ({ odStart: 0, rcNumber: '' }) as BikeRcData),
    oldRcData: existingRcData && existingRcData.length ? JSON.parse(JSON.stringify(existingRcData)) as OldBikeRcData[] : [],
    extraAmount: 0,
    oldBikeRemainingDurationAmount: 0,
    newBikeRemainingDurationAmount: 0,
    changeTime: new Date().getTime(),
    uploads: {}
  });
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Parse state or props
  const authCookie = getAuthCookie();
  const allBikeRcData = localState.rcOptions || [];
  const validRcSelectOptions = allBikeRcData.map(rc => ({
    value: rc.rcNumber,
    display: rc.conflictingOrder ? `${rc.rcNumber} (Ongoing Order ID: ${rc.conflictingOrder})` : rc.rcNumber,
    invisible: rc.used
  }));
  const bikeSelectionOptions = (localState.bikeData || []).map(bike => ({
    value: (bike.bike?.id || '').toString(),
    display: `${bike.bike?.brand} ${bike.bike?.name}`
  }))
  const selectedBike = localState.bikeData?.find(b => b.bikeId === formState.selectedBikeId);

  // Parse state to decide submit button state
  const emptyFields = parseFalsyFields([
    { name: 'OTP', value: formState.otp },
    { name: 'New Bike', value: formState.selectedBikeId },
    ...(formState.oldRcData && formState.oldRcData.length ? [
      ...(Array(bikeCount).fill(0).map((_, i) => ({ name: `Odometer reading ${i + 1}`, value: formState?.oldRcData?.[i].odEnd })))
    ] : []),
    ...(props.order.rideStatus === RideStatus.STARTED ? [
      ...(Array(bikeCount).fill(0).map((_, i) => ({ name: `RC number ${i + 1}`, value: formState?.rcData?.[i].rcNumber }))),
      ...(Array(bikeCount).fill(0).map((_, i) => ({ name: `Odometer reading ${i + 1}`, value: formState?.rcData?.[i].odStart })))
    ] : [])
  ]);
  let disableSubmit = false;
  if (emptyFields.length || formState.errors.otp) {
    disableSubmit = true;
  }

  // Load list of bikes that the vendor has
  useEffect(() => {
    if (localState.bikeData || !open) return;
    setLocalState({ ...localState, loading: true, error: undefined });
    (async () => {
      try {

        // Start API calls
        if (!props.order.bikes || !props.order.bikes.length || !props.order.bikes[0].id) {
          throw new Error('Failed to load bike data!');
        }
        const bikeDataPromise = GobikesApi.get(`/vendors/hubs/${props.hub.id}/bikes`, { headers: { 'vendor-token': authCookie }, params: { hideBlocked: true, overrides: false } });
        const rcDataPromise = GobikesApi.get(`/vendors/hubs/${props.order.hubId}/bikes/${props.order.bikes[0].id}`, {
          params: { loadRcNumbersInUse: true, startTime: props.order.rideStartScheduled, endTime: props.order.rideEndScheduled },
          headers: { 'vendor-token': authCookie }
        });

        // Load list of bikes
        const { data: response } = await bikeDataPromise;
        const bikeData: HubBikeModel[] = response.data;

        // Load RC numbers data
        const { data } = await rcDataPromise;
        if (!data.data || !data.data.length || !data.data[0].bikes) {
          throw new Error('Failed to load RC numbers data!');
        }
        const rcData = updateRcNumberConflicts(data.data[0].bikes, data.data[0].bike);

        // Set state
        setLocalState({ ...localState, rcOptions: rcData, bikeData, loading: false, error: undefined });

      } catch (e: any) {
        setLocalState({ ...localState, loading: false, error: e.message });
      }
    })();
  }, [props, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle  form submission
  async function submitEditOrderRequest(e: any) {

    // Parse input and check for errors
    e.preventDefault();
    if (emptyFields.length || formState.errors.otp) {
      setFormState({ ...formState, error: true, loading: false, message: 'Please fix all errors or missing fields' });
    }

    // Generate upload data
    const images = []
    for (const label of Object.keys(formState.uploads)) {
      if (formState.uploads[label as OrderUploadLabels])
        images.push({ label: label, data: formState.uploads[label as OrderUploadLabels] })
    }

    // Set loading mode and try to update the bikes
    setFormState({ ...formState, loading: true });
    const payload = { otp: formState.otp, oldBikeData: formState.oldRcData, bikeData: formState.rcData, bikeId: formState.selectedBikeId, changeTime: formState.changeTime, customExtraAmount: formState.extraAmount, ...(images.length && { images }) };
    try {
      await GobikesApi.put(`/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/bikes`, payload, { headers: { 'vendor-token': authCookie } });
      setFormState({ ...formState, error: false, message: 'Order updated successfully!', loading: true });
      setTimeout(() => { props.backFn() }, 2000);
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setFormState({ ...formState, error: true, message: errorMessage, loading: false })
    }
  }

  // Return JSX
  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>
      <Modal
        open={open}
        className="gbk-modal"
        onClose={handleClose}
      >
        <Box className="gbk-modal-content">
          {localState.loading || localState.error ? renderLoading() : renderStartOrderForm()}
        </Box>
      </Modal >
    </Fragment>
  )

  // setLocalState({ ...localState, rcOptions: rcData, extraPriceDetails: priceData, loading: false, error: undefined });

  // Render loading
  function renderLoading() {
    return <Paper className='gbk-paper'>
      <div className="loader">
        {localState.loading ? <CircularProgress /> : ''}
        <div className={`gbk-message ${localState.error ? 'error' : ''}`}>
          {localState.error}
        </div>
        <div
          style={{ position: 'absolute', top: '10px', right: '10px' }}
          onClick={() => {
            setLocalState({ ...localState, loading: false, error: undefined })
          }}
        >
          <Cancel color={'error'} />
        </div>
      </div>
    </Paper>
  }

  // Render start order form
  function renderStartOrderForm() {

    // Upload styles
    const uploadStyles: SxProps = {
      width: '200px',
      marginBottom: '20px'
    }

    // Return content
    return <Paper className='gbk-paper'>

      {/* Header */}
      <div className="header">
        <Typography className="header-left" ><b>Change Vehicle</b></Typography>
        <IconButton className="header-right" onClick={handleClose} style={{ padding: 0 }}>
          <Close />
        </IconButton>
      </div>

      {/* Forms */}
      <div className="content">
        <FormControl sx={{ margin: '30px 30px', width: '80%', display: 'block' }} >

          {/* Old Vehicle Details */}
          {formState.oldRcData && formState.oldRcData.length ? (
            <Fragment>
              <Typography sx={{ fontSize: '16px' }}><b>Old Vehicle Details</b></Typography>
              <br />
              {formState.oldRcData.map((bikeDetail, i) => (
                <div key={i} className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    label={`End Odometer Reading for ${bikeDetail.rcNumber}`}
                    editMode={true}
                    value={formState?.oldRcData?.[i].odEnd || ''}
                    validateFn={greaterThanValueValidationFn(existingRcData?.[i].odStart || 0)}
                    updateFn={(v: number) => {
                      const newOldRcData = formState.oldRcData || [];
                      newOldRcData[i].odEnd = v;
                      setFormState({ ...formState, oldRcData: newOldRcData })
                    }}
                    useInternalLabel={true}
                  />
                  <span className="inb pad-5 error-text fs-16">*</span>
                </div>
              ))}
              <br /><br />
            </Fragment>
          ) : ''}

          {/* Select bike */}
          <Typography sx={{ fontSize: '16px' }}><b>Select New Vehicle</b></Typography>
          <br />
          <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
            <GobikesField
              type={GbkFieldType.SELECT}
              editMode={true}
              label="Select vehicle name"
              value={formState?.selectedBikeId || ''}
              selectOptions={bikeSelectionOptions}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              updateFn={(v: string) => {
                const newBikeId = v ? Number(v) : undefined;
                const baseRcData = Array(bikeCount).fill(0).map(a => ({ odStart: 0, rcNumber: '' }) as BikeRcData);
                if (newBikeId) {
                  let priceCheckRequired = false;
                  if (bikeId && Number(bikeId) !== newBikeId) {
                    priceCheckRequired = true;
                  }
                  setLocalState({ ...localState, loading: true, error: undefined });
                  (async () => {
                    try {

                      // Load RC numbers data
                      const { data } = await GobikesApi.get(`/vendors/hubs/${props.order.hubId}/bikes/${newBikeId}`, {
                        params: { loadRcNumbersInUse: true, startTime: props.order.rideStartScheduled, endTime: props.order.rideEndScheduled },
                        headers: { 'vendor-token': authCookie }
                      });
                      if (!data.data || !data.data.length || !data.data[0].bikes) {
                        throw new Error('Failed to load RC numbers data!');
                      }
                      const rcData = updateRcNumberConflicts(data.data[0].bikes, data.data[0].bike);

                      // Get price if required
                      let extraAmount = 0;
                      let oldBikeRemainingDurationAmount = 0;
                      let newBikeRemainingDurationAmount = 0;
                      let effectiveChangeTime = formState.changeTime;
                      if (priceCheckRequired) {
                        // const { data: priceCalculationData } = await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/check-price`, {
                        const { data: response } = await GobikesApi.put(`/vendors/hubs/${selectedHub.id}/orders/${props.order.id}/bikes`, {
                          bikeId: newBikeId || bikeId,
                          changeTime: formState.changeTime,
                          calculateOnly: true
                        }, {
                          headers: { 'vendor-token': authCookie }
                        })
                        extraAmount = response?.data?.extraAmount || 0;
                        oldBikeRemainingDurationAmount = response?.data?.oldBikeAmountForRemainingDuration || 0;
                        newBikeRemainingDurationAmount = toFixed2(Math.abs(extraAmount + oldBikeRemainingDurationAmount));
                        effectiveChangeTime = response?.data?.changeTime;
                      }

                      // Set state
                      setLocalState({ ...localState, rcOptions: rcData, loading: false, error: undefined });
                      setFormState({
                        ...formState,
                        selectedBikeId: v ? Number(v) : undefined,
                        rcData: baseRcData,
                        extraAmount,
                        oldBikeRemainingDurationAmount,
                        newBikeRemainingDurationAmount,
                        changeTime: effectiveChangeTime
                      });
                    } catch (e: any) {
                      const errorString = e.response && e.response.data ? e.response.data.message : e.message;
                      setLocalState({ ...localState, loading: false, error: errorString });
                    }
                  })();
                }

                // Clear form data
                else {
                  setLocalState({ ...localState, rcOptions: [], loading: false, error: undefined });
                  setFormState({
                    ...formState,
                    selectedBikeId: newBikeId,
                    rcData: baseRcData
                  });
                }
              }}
              useInternalLabel={true}
            />
            <span className="inb pad-5 error-text fs-16">*</span>
            <br /><br />
          </div>

          {/* New Vehicle Details */}
          {props.order.rideStatus === RideStatus.STARTED ? (
            <Fragment>
              <Typography sx={{ fontSize: '16px' }}><b>New Vehicle Details</b></Typography>
              <br />
              {Array(bikeCount).fill(0).map((_, i) => (
                <div key={i} className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
                  <GobikesField
                    type={GbkFieldType.SELECT}
                    label={`Select Vehicle Number ${i + 1}`}
                    editMode={true}
                    value={formState?.rcData?.[i].rcNumber}
                    selectOptions={validRcSelectOptions}
                    updateFn={(v: string) => {
                      const newFormRcData = formState.rcData || [];
                      const newRcData = localState.rcOptions?.map(d => {
                        if (d.rcNumber === formState?.rcData?.[i].rcNumber) d.used = false;
                        if (d.rcNumber === v) d.used = true;
                        return d;
                      });
                      setLocalState({ ...localState, rcOptions: newRcData })
                      newFormRcData[i].rcNumber = v;
                      setFormState({ ...formState, rcData: newFormRcData });
                    }}
                    useInternalLabel={true}
                  />
                  <span className="inb pad-5 error-text fs-16">*</span>
                  <br /><br /><br />
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    label={`Start Odometer Reading ${i + 1}`}
                    editMode={true}
                    value={formState?.rcData?.[i].odStart || ''}
                    validateFn={greaterThanValueValidationFn(0)}
                    updateFn={(v: number) => {
                      const newRcData = formState.rcData || [];
                      newRcData[i].odStart = v;
                      setFormState({ ...formState, rcData: newRcData })
                    }}
                    useInternalLabel={true}
                  />
                  <span className="inb pad-5 error-text fs-16">*</span>
                </div>
              ))}
              <br /><br />
            </Fragment>
          ) : ''}

          {/* Price details */}
          <Typography sx={{ fontSize: '16px' }}><b>Payment Details</b></Typography>
          <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
            <GobikesField
              type={GbkFieldType.CHECKBOX}
              label={'Use custom amount'}
              editMode={true}
              disabled={false}
              value={formState.useCustomAmount}
              updateFn={(v: boolean) => {
                setFormState({ ...formState, useCustomAmount: v })
              }}
              useInternalLabel={true}
            />
            <br /><br />
            {formState.useCustomAmount ? (
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={<span>{formState.extraAmount >= 0 ? 'Extra amount to be collected' : <span>Amount to be <b>refunded</b></span>}</span>}
                editMode={true}
                disabled={!formState.useCustomAmount}
                displayPrefix="₹ "
                value={formState.extraAmount}
                validateFn={greaterThanEqualToValueValidationFn(0)}
                updateFn={(v: number) => {
                  console.log(v)
                  setFormState({ ...formState, extraAmount: v })
                }}
                useInternalLabel={true}
              />
            ) : ''}
            {formState.useCustomAmount ? '' : (
              <Typography>
                <span className="grey-text-dark">{bikeName} (Old)</span><br />
                From {toIstDateString(formState.changeTime, { removeDay: true })} to&nbsp;
                {toIstDateString(Number(props.order.rideEndScheduled), { removeDay: true })}
                : <br /><b>₹ {formState.oldBikeRemainingDurationAmount}</b>
                <br /><br />
                <span className="grey-text-dark">{selectedBike ? `${selectedBike.bike?.brand} ${selectedBike.bike?.name}` : 'New Vehicle'} (New)</span><br />
                From {toIstDateString(formState.changeTime, { removeDay: true })} to&nbsp;
                {toIstDateString(Number(props.order.rideEndScheduled), { removeDay: true })}
                : <br /><b>₹ {formState.newBikeRemainingDurationAmount}</b>
                <br /><br />
                <span className="grey-text-dark">Total {formState.extraAmount > 0 ? 'extra payment' : 'refund'}:
                  <br />
                  <b className={formState.extraAmount < 0 ? 'error-text' : 'success-text'}> ₹ {Math.abs(formState.extraAmount)} </b>
                </span>
              </Typography>
            )}

            {/* Uploads */}
            {props.order.rideStatus === RideStatus.NOT_STARTED ? '' : (
              <Fragment>
                <br /><br /><br />
                <Typography sx={{ fontSize: '16px' }}><b>Uploads</b></Typography>
                <br /><br />
                <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
                  <GobikesField
                    label="Customer Photo"
                    type={GbkFieldType.UPLOAD}
                    tag={OrderUploadLabels.CUSTOMER}
                    gbkFieldSx={uploadStyles}
                    editMode={true}
                    value={formState.uploads.CUSTOMER || ''}
                    updateFn={v => {
                      setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.CUSTOMER]: v } })
                    }}
                  />
                  <GobikesField
                    label="Bike Left"
                    type={GbkFieldType.UPLOAD}
                    tag={OrderUploadLabels.BIKE_LEFT}
                    gbkFieldSx={uploadStyles}
                    editMode={true}
                    value={formState.uploads.BIKE_LEFT || ''}
                    updateFn={v => {
                      setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_LEFT]: v } })
                    }}
                  />
                  <GobikesField
                    label="Bike Right"
                    type={GbkFieldType.UPLOAD}
                    tag={OrderUploadLabels.BIKE_RIGHT}
                    gbkFieldSx={uploadStyles}
                    editMode={true}
                    value={formState.uploads.BIKE_RIGHT || ''}
                    updateFn={v => {
                      setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_RIGHT]: v } })
                    }}
                  />
                  <GobikesField
                    label="Bike Front"
                    type={GbkFieldType.UPLOAD}
                    tag={OrderUploadLabels.BIKE_FRONT}
                    gbkFieldSx={uploadStyles}
                    editMode={true}
                    value={formState.uploads.BIKE_FRONT || ''}
                    updateFn={v => {
                      setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_FRONT]: v } })
                    }}
                  />
                  <GobikesField
                    label="Bike Back"
                    type={GbkFieldType.UPLOAD}
                    tag={OrderUploadLabels.BIKE_BACK}
                    gbkFieldSx={uploadStyles}
                    editMode={true}
                    value={formState.uploads.BIKE_BACK || ''}
                    updateFn={v => {
                      setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.BIKE_BACK]: v } })
                    }}
                  />
                  <GobikesField
                    label="Odometer"
                    type={GbkFieldType.UPLOAD}
                    tag={OrderUploadLabels.ODOMETER}
                    gbkFieldSx={uploadStyles}
                    editMode={true}
                    value={formState.uploads.ODOMETER || ''}
                    updateFn={v => {
                      setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.ODOMETER]: v } })
                    }}
                  />
                  <GobikesField
                    label="Other"
                    type={GbkFieldType.UPLOAD}
                    tag={OrderUploadLabels.OTHER}
                    gbkFieldSx={uploadStyles}
                    editMode={true}
                    value={formState.uploads.OTHER || ''}
                    updateFn={v => {
                      setFormState({ ...formState, uploads: { ...formState.uploads, [OrderUploadLabels.OTHER]: v } })
                    }}
                  />
                </div>
              </Fragment>
            )}

            <Typography>
              {/* {localState.extraPriceDetails ? (
                <span>
                  {
                    Math.abs(props.order.totalPayableAmount - localState.extraPriceDetails.totalPayableAmount) < Number.EPSILON
                      ? 'No extra amount to be collected'
                      : (
                        <span>
                          <div style={{ borderRadius: '6px', background: '#fafafa', border: '1px solid #ddd', padding: '10px' }}>
                            <span>
                              <b>Old bike: </b>
                              <br />
                              <span className="grey-text-medium">From: </span>
                              {DateTime.fromMillis(Number(props.order.rideStartScheduled)).toFormat('hh:mm a, dd LLL yyyy')}
                              <br />
                              <span className="grey-text-medium">To: </span>
                              {DateTime.fromMillis(bikeChangeTime).toFormat('hh:mm a, dd LLL yyyy')}
                              <br />
                              <span className="grey-text-medium">Amount: </span>
                              <b>₹ {localState.extraPriceDetails.bikeUpdateDetails.oldBikePayableAmount}</b>
                            </span>
                            <br />
                            <br />
                            <span>
                              <b>New bike:</b>
                              <br />
                              <span className="grey-text-medium">From: </span>
                              {DateTime.fromMillis(bikeChangeTime).toFormat('hh:mm a, dd LLL yyyy')}
                              <br />
                              <span className="grey-text-medium">To: </span>
                              {DateTime.fromMillis(Number(props.order.rideEndScheduled)).toFormat('hh:mm a, dd LLL yyyy')}
                              <br />
                              <span className="grey-text-medium">Amount: </span>
                              <b>₹ {localState.extraPriceDetails.bikeUpdateDetails.newBikePayableAmount}</b>
                            </span>
                          </div>
                          <br />
                          <span style={{ display: 'inline-block', width: '180px' }}>
                            New rental amount:
                          </span>
                          <span style={{ display: 'inline-block', textAlign: 'right', width: '100px' }}>
                            <b>₹ {localState.extraPriceDetails.bikeUpdateDetails.totalPayableAmount.toFixed(2)}</b>
                          </span>
                          <br />
                          <span style={{ display: 'inline-block', width: '180px' }}>
                            Old rental amount:
                          </span>
                          <span style={{ display: 'inline-block', textAlign: 'right', width: '100px' }}>
                            <b>- ₹ {props.order.totalPayableAmount.toFixed(2)}</b>
                          </span>
                          <br />
                          <hr />
                          <span style={{ display: 'inline-block', width: '180px' }}>
                            {props.order.totalPayableAmount > localState.extraPriceDetails.bikeUpdateDetails.totalPayableAmount ? 'Amount to be refunded offline' : 'Extra amount to be collected offline'}
                            :
                          </span>
                          <span
                            style={{
                              display: 'inline-block',
                              textAlign: 'right',
                              width: '100px',
                              color: props.order.totalPayableAmount > localState.extraPriceDetails.bikeUpdateDetails.totalPayableAmount ? Colors.failureRed : Colors.primaryDark
                            }}
                          >
                            <b>
                              ₹ {(Math.abs(props.order.totalPayableAmount - localState.extraPriceDetails.bikeUpdateDetails.totalPayableAmount)).toFixed(2)}
                            </b>
                          </span>
                        </span>
                      )
                  }
                </span>
              ) : 'No extra amount to be collected'} */}
            </Typography>
          </div>
          <br /><br />

          {/* Booking details - OTP */}
          <Typography sx={{ fontSize: '16px' }}><b>OTP Verification</b></Typography>
          <br />
          <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'OTP'}
              editMode={true}
              value={formState.otp || ''}
              validateFn={exactDigitCountValidationFn(4)}
              updateFn={(v: string, error?: string) => {
                setFormState({ ...formState, otp: v.replace(/[^0-9]/g, ''), errors: { ...formState.errors, otp: error } })
              }}
              useInternalLabel={true}
            />
            <span className="inb pad-5 error-text fs-16">*</span>
          </div>

          {/* Resend OTP */}
          <div className="gbk-col" style={{ width: 'calc(100% - 10px)' }}>
            <div className="gbk-field" style={{ marginTop: 0 }}>
              <LoadingButton
                startIcon={localState.smsState === 'DONE' ? <DoneAll /> : <Refresh />}
                variant={'outlined'}
                className="thick-border"
                loading={localState.smsState === 'PROGRESS'}
                disabled={localState.smsState === 'DONE'}
                onClick={async () => {
                  setLocalState({ ...localState, smsState: 'PROGRESS' });
                  try {
                    await GobikesApi.post(`/vendors/hubs/${props.hub.id}/orders/${props.order.id}/send-change-bike-otp-sms`, { userId: props.order.userId }, { headers: { 'vendor-token': authCookie } });
                    setLocalState({ ...localState, smsState: 'DONE' })

                  } catch (e: any) {
                    const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
                    setLocalState({ ...localState, error: errorMessage, smsState: 'DONE' })
                  }
                }}
              >
                {localState.smsState === 'DONE' ? 'OTP Sent Successfully' : 'Send OTP'}
              </LoadingButton>
            </div>
          </div>

          {/* Message */}
          <div className={`gbk-login-message ${formState.error ? 'error' : ''}`}>{formState.message}</div>
        </FormControl>
      </div>

      {/* Footer */}
      <div className="footer">
        <form onSubmit={submitEditOrderRequest}>
          <LoadingButton
            variant='contained'
            type='submit'
            ref={submitButtonRef}
            loading={formState.loading}
            className="no-text-transform"
            sx={{ width: 'calc(100% - 20px)', ml: '10px' }}
            disabled={disableSubmit}
          >
            Update Booking
          </LoadingButton>
          &nbsp;&nbsp;
        </form>
      </div>
    </Paper>
  }

}

// Utility function to calculate RC Number conflicts
function updateRcNumberConflicts(rcData: any, bikeData: any) {
  const rcOrderMap: { [k: string]: number[] } = {};
  if (bikeData && bikeData.orders) {
    for (const order of bikeData.orders) {
      if (order.bikeDetails && order.bikeDetails.length) {
        for (const bikeDetail of order.bikeDetails) {
          if (!rcOrderMap[bikeDetail.rcNumber]) rcOrderMap[bikeDetail.rcNumber] = [];
          rcOrderMap[bikeDetail.rcNumber].push(order.id);
        }
      }
    }
  }
  for (const rcDatum of rcData) {
    if (rcOrderMap[rcDatum.rcNumber]) {
      rcDatum.conflictingOrder = rcOrderMap[rcDatum.rcNumber][0];
    }
  }
  return rcData;
}

